import React, { useContext, useEffect, useRef, useState } from 'react';
import { Checkbox, CircularProgress, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    StyledAddBasketTitle,
    StyledCameraIcon,
    StyledColumnBox,
    StyledDeleteLink,
    StyledGreenButton,
    StyledIconButton,
    StyledImage,
    StyledImageBox,
    StyledMediumTypography,
    StyledModalButtons,
    StyledNormalTypography,
    StyledRowBox,
    StyledWhiteButton
} from '../../theme/StyledElements';
import { TextInputField } from '../../components/TextInputField';
import { Paniers } from '../../models/Paniers';
import { Add, Remove } from '@mui/icons-material';
import cameraIcon from '../../assets/camera.svg';
import { getSpecificBasket, saveBasket } from '../../services/cloudFirestore';
import { UserContext } from '../../providers/UserProvider';
import { PartnerProducer } from '../../models/PartnerProducer';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddProducerPartner } from '../../components/Basket/AddProducerPartner';
import { BasketPublishModal } from '../../components/Basket/BasketPublishModal';
import { getImageKitUrlFrom, producerPartnerCount } from '../../utils';
import { AddButton } from '../../components/AddButton';
import { InputComponent } from '../../components/InputComponent';
import { useMountedRef } from '../../hooks/useMountedRef';

const StyledBox = styled(StyledColumnBox)(() => ({
    alignItems: 'left',
    marginBottom: '60px'
}));

const StyledBasketBox = styled(StyledBox)(() => ({
    marginTop: '40px',
    maxWidth: '745px',
    marginLeft: '10px'
}));

const StyledIncrementDecrementButton = styled(StyledGreenButton)(() => ({
    height: '56px',
    width: '56px',
    margin: '8px'
}));

const StyledQuantityBox = styled(StyledNormalTypography)(() => ({
    width: '125px',
    marginBottom: '15px',
    height: '56px',
    marginTop: '8px',
    padding: '17px',
    border: '1px solid #707070',
    textAlign: 'center'
}));

export function AddBasket(): React.ReactElement {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const mounted = useMountedRef();
    const user = useContext(UserContext);
    const [values, setValues] = useState<Paniers>();
    const [id, setId] = useState<string | unknown>();
    const [basketId, setBasketId] = useState<string>();
    const [disabled, setDisabled] = useState<boolean>(true);
    const [addSharedBasket, setAddSharedBasket] = useState<boolean>(false);
    const [basketPhoto1, setBasketPhoto1] = useState<string>();
    const [basketPhoto2, setBasketPhoto2] = useState<string>();
    const [basketPhoto3, setBasketPhoto3] = useState<string>();
    const basketPhoto1File = useRef<HTMLInputElement>(null);
    const basketPhoto2File = useRef<HTMLInputElement>(null);
    const basketPhoto3File = useRef<HTMLInputElement>(null);
    const [partnerProducer, setPartnerProducer] = useState<PartnerProducer[]>([
        {
            nom: '',
            prix: undefined
        }
    ]);
    const [publishSuccess, setPublishSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [disableButton, setDisableButton] = useState<boolean>(true);

    useEffect(() => {
        if (location.state) {
            setId(location.state);
        }
    }, [location]);

    useEffect(() => {
        if (addSharedBasket) {
            setDisableButton(false);
        } else {
            setDisableButton(true);
        }
    }, [addSharedBasket]);

    useEffect(() => {
        if (id && typeof id === 'string' && mounted.current) {
            getSpecificBasket(id).then((dataList) => {
                if (dataList && mounted.current) {
                    setValues({
                        idPanier: dataList.idPanier,
                        idProducteur: dataList.idProducteur,
                        titre: dataList.titre,
                        poids: dataList.poids,
                        prix: dataList.prix,
                        quantite: dataList.quantite,
                        photoUrl: dataList.photoUrl,
                        detail: dataList.detail,
                        estPublie: dataList.estPublie,
                        satisfaction: dataList.satisfaction,
                        paniersVendus: dataList.paniersVendus,
                        ventes: dataList.ventes,
                        producteurPartenaire: dataList.producteurPartenaire
                    });
                    if (dataList.idPanier && mounted.current) {
                        setBasketId(dataList.idPanier);
                    }
                    if (dataList.photoUrl && mounted.current) {
                        dataList.photoUrl.forEach((item, index) => {
                            if (typeof item === 'string') {
                                if (index === 0) {
                                    setBasketPhoto1(item);
                                }
                                if (index === 1) {
                                    setBasketPhoto2(item);
                                }
                                if (index === 2) {
                                    setBasketPhoto3(item);
                                }
                            }
                        });
                    }
                    if (
                        dataList.producteurPartenaire &&
                        dataList.producteurPartenaire?.length > 0 &&
                        mounted.current
                    ) {
                        setAddSharedBasket(true);
                        setPartnerProducer(dataList.producteurPartenaire);
                    }
                }
            });
        }
    }, [id]);

    const onTextFieldChange = (
        state: string,
        value: string | number | File[] | PartnerProducer[] | string[]
    ): void => {
        setValues({ ...values, [state]: value });
    };

    useEffect(() => {
        if (
            values?.titre &&
            values?.poids &&
            values?.prix &&
            values?.quantite &&
            values?.detail &&
            values?.photoUrl &&
            (values?.photoUrl[0] || values?.photoUrl[1] || values?.photoUrl[2])
        ) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [values]);

    const updateQuantity = (operation: string): void => {
        const data = values?.quantite ?? 0;
        if (operation === 'decrement' && data > 0) {
            setValues({ ...values, quantite: data - 1 });
        } else if (operation === 'increment') {
            setValues({ ...values, quantite: data + 1 });
        }
    };

    const triggerBasketPhoto1Upload = (): void => {
        if (basketPhoto1File && basketPhoto1File.current) {
            basketPhoto1File.current.click();
        }
    };

    const triggerBasketPhoto2Upload = (): void => {
        if (basketPhoto2File && basketPhoto2File.current) {
            basketPhoto2File.current.click();
        }
    };

    const triggerBasketPhoto3Upload = (): void => {
        if (basketPhoto3File && basketPhoto3File.current) {
            basketPhoto3File.current.click();
        }
    };

    const updateBasketPhoto = (
        photoName: string,
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        if (event.target.files) {
            const list = values?.photoUrl ?? [];
            if (photoName === 'basketPhoto1') {
                list[0] = event.target.files[0];
                setBasketPhoto1(URL.createObjectURL(event.target.files[0]));
            }
            if (photoName === 'basketPhoto2') {
                list[1] = event.target.files[0];
                setBasketPhoto2(URL.createObjectURL(event.target.files[0]));
            }
            if (photoName === 'basketPhoto3') {
                list[2] = event.target.files[0];
                setBasketPhoto3(URL.createObjectURL(event.target.files[0]));
            }
            onTextFieldChange('photoUrl', list);
        }
    };

    const removeBasketPhoto = (photoName: string): void => {
        const list = values?.photoUrl ?? [];
        if (photoName === 'basketPhoto1') {
            list[0] = '';
            setBasketPhoto1('');
        }
        if (photoName === 'basketPhoto2') {
            list[1] = '';
            setBasketPhoto2('');
        }
        if (photoName === 'basketPhoto3') {
            list[2] = '';
            setBasketPhoto3('');
        }
        onTextFieldChange('photoUrl', list);
    };

    const publishBasket = async (): Promise<void> => {
        setLoading(true);
        if (user && values && user.idProducteur) {
            const data = values;
            data.idProducteur = user.idProducteur;
            const idBasket = await saveBasket(data);
            setLoading(false);
            if (idBasket) {
                setBasketId(idBasket);
            }
            setPublishSuccess(true);
        }
    };

    const addProducerPartner = (): void => {
        setPartnerProducer([
            ...partnerProducer,
            {
                nom: undefined,
                prix: undefined
            }
        ]);
    };

    const saveProducerPartner = (value: number | string, index: number): void => {
        if (typeof value === 'string') {
            partnerProducer[index].nom = value;
        } else {
            partnerProducer[index].prix = value;
        }
        setPartnerProducer([...partnerProducer]);
        onTextFieldChange(
            'producteurPartenaire',
            partnerProducer.filter((producer) => producer.nom && producer.prix)
        );
    };

    const partnerProducerList = (): JSX.Element[] => {
        const divList = [];
        for (let i = 0; i < partnerProducer.length; i += 1) {
            divList.push(
                <AddProducerPartner
                    key={i}
                    index={i}
                    partnerProducer={partnerProducer[i]}
                    saveProducerPartner={(value: number | string): void =>
                        saveProducerPartner(value, i)
                    }
                    partnerCount={producerPartnerCount(t)[i]}
                />
            );
        }
        return divList;
    };

    return (
        <StyledBasketBox>
            <BasketPublishModal
                isModelOpen={publishSuccess}
                setModelOpen={setPublishSuccess}
                basketId={basketId}
                onButtonClick={(): void => {
                    setPublishSuccess(false);
                    if (basketId) {
                        navigate(`/view-basket/${basketId}`);
                    } else {
                        navigate('/add-basket');
                    }
                }}
            />
            <StyledAddBasketTitle variant="caption">{t('cartTitle')}*</StyledAddBasketTitle>
            <InputComponent
                type="text"
                name="titre"
                placeholder={t('cartTitle')}
                value={values?.titre}
                onTextChange={(value: string): void => onTextFieldChange('titre', value)}
                width="530px"
            />
            <StyledAddBasketTitle variant="caption">
                {t('basketWeightRequired')}
            </StyledAddBasketTitle>
            <StyledRowBox>
                <InputComponent
                    type="number"
                    name="poids"
                    placeholder={t('basketWeightRequired')}
                    value={values?.poids}
                    onTextChange={(value: string): void =>
                        onTextFieldChange('poids', parseFloat(value))
                    }
                    width="315px"
                />
                <StyledNormalTypography variant="h3" sx={{ marginTop: '27px', marginLeft: '10px' }}>
                    {t('kg')}
                </StyledNormalTypography>
            </StyledRowBox>
            <StyledAddBasketTitle variant="caption">
                {t('basketPriceRequired')}
            </StyledAddBasketTitle>
            <StyledRowBox>
                <InputComponent
                    type="number"
                    name="prix"
                    placeholder={t('basketPriceRequired')}
                    value={values?.prix}
                    onTextChange={(value: string): void =>
                        onTextFieldChange('prix', parseFloat(value))
                    }
                    width="205px"
                />
                <StyledNormalTypography variant="h3" sx={{ marginTop: '27px', marginLeft: '10px' }}>
                    {t('euros')}
                </StyledNormalTypography>
            </StyledRowBox>
            <StyledAddBasketTitle variant="caption">
                {t('basketQuantityForSaleRequired')}
            </StyledAddBasketTitle>
            <StyledRowBox>
                <StyledIncrementDecrementButton onClick={(): void => updateQuantity('decrement')}>
                    <Remove />
                </StyledIncrementDecrementButton>
                <StyledQuantityBox variant="h3">{values?.quantite ?? 0}</StyledQuantityBox>
                <StyledIncrementDecrementButton onClick={(): void => updateQuantity('increment')}>
                    <Add />
                </StyledIncrementDecrementButton>
            </StyledRowBox>
            <StyledAddBasketTitle variant="caption">{t('basketPhotos')}</StyledAddBasketTitle>
            <StyledRowBox>
                <StyledImageBox sx={{ border: basketPhoto1 ? 'none' : '1px solid #394A35' }}>
                    {!basketPhoto1 && (
                        <div>
                            <input
                                type="file"
                                ref={basketPhoto1File}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                    updateBasketPhoto('basketPhoto1', event)
                                }
                                accept="image/*"
                                multiple={false}
                                style={{ display: 'none' }}
                            />
                            <StyledIconButton
                                onClick={triggerBasketPhoto1Upload}
                                sx={{ flexDirection: 'column' }}>
                                <StyledCameraIcon src={cameraIcon} alt="imageUpload" />
                                <StyledMediumTypography variant="caption">
                                    {t('addPicture')}
                                </StyledMediumTypography>
                            </StyledIconButton>
                        </div>
                    )}
                    {basketPhoto1 && (
                        <div>
                            <StyledImage src={getImageKitUrlFrom(basketPhoto1)} alt="imageUpload" />
                            <StyledDeleteLink
                                type="button"
                                onClick={(): void => {
                                    removeBasketPhoto('basketPhoto1');
                                }}
                                sx={{ marginBottom: '18px' }}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        textTransform: 'none',
                                        letterSpacing: 0,
                                        fontWeight: 500
                                    }}>
                                    {t('deletePhoto')}
                                </Typography>
                            </StyledDeleteLink>
                        </div>
                    )}
                </StyledImageBox>
                <StyledImageBox sx={{ border: basketPhoto2 ? 'none' : '1px solid #394A35' }}>
                    {!basketPhoto2 && (
                        <div>
                            <input
                                type="file"
                                ref={basketPhoto2File}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                    updateBasketPhoto('basketPhoto2', event)
                                }
                                accept="image/*"
                                multiple={false}
                                style={{ display: 'none' }}
                            />
                            <StyledIconButton
                                onClick={triggerBasketPhoto2Upload}
                                sx={{ flexDirection: 'column' }}>
                                <StyledCameraIcon src={cameraIcon} alt="imageUpload" />
                                <StyledMediumTypography variant="caption">
                                    {t('addPicture')}
                                </StyledMediumTypography>
                            </StyledIconButton>
                        </div>
                    )}
                    {basketPhoto2 && (
                        <div>
                            <StyledImage src={getImageKitUrlFrom(basketPhoto2)} alt="imageUpload" />
                            <StyledDeleteLink
                                type="button"
                                onClick={(): void => {
                                    removeBasketPhoto('basketPhoto2');
                                }}
                                sx={{ marginBottom: '18px' }}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        textTransform: 'none',
                                        letterSpacing: 0,
                                        fontWeight: 500
                                    }}>
                                    {t('deletePhoto')}
                                </Typography>
                            </StyledDeleteLink>
                        </div>
                    )}
                </StyledImageBox>
                <StyledImageBox sx={{ border: basketPhoto3 ? 'none' : '1px solid #394A35' }}>
                    {!basketPhoto3 && (
                        <div>
                            <input
                                type="file"
                                ref={basketPhoto3File}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                    updateBasketPhoto('basketPhoto3', event)
                                }
                                accept="image/*"
                                multiple={false}
                                style={{ display: 'none' }}
                            />
                            <StyledIconButton
                                onClick={triggerBasketPhoto3Upload}
                                sx={{ flexDirection: 'column' }}>
                                <StyledCameraIcon src={cameraIcon} alt="imageUpload" />
                                <StyledMediumTypography variant="caption">
                                    {t('addPicture')}
                                </StyledMediumTypography>
                            </StyledIconButton>
                        </div>
                    )}
                    {basketPhoto3 && (
                        <div>
                            <StyledImage src={getImageKitUrlFrom(basketPhoto3)} alt="imageUpload" />
                            <StyledDeleteLink
                                type="button"
                                onClick={(): void => {
                                    removeBasketPhoto('basketPhoto3');
                                }}
                                sx={{ marginBottom: '18px' }}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        textTransform: 'none',
                                        letterSpacing: 0,
                                        fontWeight: 500
                                    }}>
                                    {t('deletePhoto')}
                                </Typography>
                            </StyledDeleteLink>
                        </div>
                    )}
                </StyledImageBox>
            </StyledRowBox>
            <StyledAddBasketTitle variant="caption">
                {t('basketDescriptionRequired')}
            </StyledAddBasketTitle>
            <TextInputField
                multiline
                rows={16}
                id="detail"
                name="detail"
                type="text"
                placeholder={t('basketDescriptionRequired')}
                value={values?.detail}
                onChange={(e): void => onTextFieldChange('detail', e.currentTarget.value)}
                sx={{
                    width: '650px',
                    marginBottom: '15px',
                    backgroundColor: '#0000000B',
                    color: '#00000099',
                    font: 'normal normal normal 24px Roboto',
                    border: 'none'
                }}
            />
            <StyledRowBox>
                <Checkbox
                    id="acceptConditions"
                    name="acceptConditions"
                    checked={addSharedBasket}
                    onChange={(): void => {
                        setAddSharedBasket(!addSharedBasket);
                    }}
                    sx={{
                        color: '#134C03',
                        '&.Mui-checked': {
                            color: '#134C03'
                        }
                    }}
                />
                <StyledNormalTypography
                    variant="caption"
                    sx={{
                        marginTop: '15px'
                    }}>
                    {t('createdSharedBasket')}
                </StyledNormalTypography>
            </StyledRowBox>
            {addSharedBasket && partnerProducerList()}
            {partnerProducer.length > 0 && (
                <AddButton
                    disabled={disableButton}
                    title={t('addPartnerProducer')}
                    onButtonClick={(): void => addProducerPartner()}
                    sx={{ marginTop: '20px' }}
                />
            )}
            <StyledModalButtons sx={{ justifyContent: 'flex-end', margin: '20px 0px 20px 0px' }}>
                {id && (
                    <StyledWhiteButton onClick={(): void => navigate(`/view-basket/${basketId}`)}>
                        <StyledMediumTypography variant="caption">
                            {t('cancel')}
                        </StyledMediumTypography>
                    </StyledWhiteButton>
                )}
                {loading && (
                    <CircularProgress
                        style={{
                            color: '#134C03',
                            height: '25px',
                            width: '25px',
                            marginTop: '25px'
                        }}
                    />
                )}
                {!loading && (
                    <StyledGreenButton
                        disabled={disabled}
                        sx={{ opacity: disabled ? 0.5 : 1 }}
                        onClick={(): Promise<void> => publishBasket()}>
                        <StyledMediumTypography
                            variant="caption"
                            color="#FFFFFF"
                            sx={{ textTransform: 'uppercase' }}>
                            {id ? t('save') : t('publish')}
                        </StyledMediumTypography>
                    </StyledGreenButton>
                )}
            </StyledModalButtons>
        </StyledBasketBox>
    );
}
